import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import Container from "../../../components/Container";
import AlertMsg from "../../../components/AlertMsg";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";

import { redirectUriSettingsService } from "../../../api/useQueryHooks";
import { QUERY_KEYS } from "../../../api/QueryKeys";

import { redirectUriSettingsSchema } from "./schemas";

const RedirectUriSettings = () => {
  const navigate = useNavigate();
  const { rest_id: restaurantId } = useParams();
  const queryClient = useQueryClient();

  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(redirectUriSettingsSchema),
    mode: "onChange",
    defaultValues: { allowedRedirectUris: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "allowedRedirectUris",
  });

  const {
    data: uris,
    isLoading: isLoadingUris,
    error: urisError,
  } = redirectUriSettingsService.useGetRedirectUris(restaurantId);

  const { mutate: updateRedirectUris, isPending: isUpdating } =
    redirectUriSettingsService.useUpdateRedirectUris(
      (response) => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.allowedRedirectUris, restaurantId],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.restaurantData, restaurantId],
        });
        setMsgAlert({
          open: true,
          message: response.msg || "Redirect URIs updated successfully!",
          msgType: "success",
        });
        reset(undefined, { keepValues: true });
      },
      (error) => {
        setMsgAlert({
          open: true,
          message: error.message || "Failed to update Redirect URIs",
          msgType: "error",
        });
      }
    );

  // Populate the form once the restaurant's redirect URIs are loaded
  useEffect(() => {
    reset({
      allowedRedirectUris: (uris || []).map((value) => ({ value })),
    });
  }, [uris, reset]);

  useEffect(() => {
    if (urisError) {
      setMsgAlert({
        open: true,
        message: urisError.message || "Failed to load Redirect URIs",
        msgType: "error",
      });
    }
  }, [urisError]);

  const onSubmit = (data) => {
    const cleaned = data.allowedRedirectUris
      .map((x) => x.value.trim())
      .filter(Boolean);
    // De-duplicate while preserving order
    const deduped = [...new Set(cleaned)];
    updateRedirectUris({ restaurantId, data: deduped });
  };

  const handleCloseAlert = () =>
    setMsgAlert({ open: false, message: "", msgType: "error" });

  const isLoading = isLoadingUris || isUpdating;

  return (
    <Container page={"restaurants"} searchVal={""} onSearch={() => null} searchLabel="">
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Allowed Redirect URIs"}
              showTitleOnly={true}
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
            />
          </Grid>
          <Grid item xs={12} px={3} pt={2}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">Allowed Redirect URIs</Typography>
                  <Typography variant="caption" color="text.secondary">
                    OAuth redirect URIs this restaurant is allowed to use.
                  </Typography>
                </Box>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => append({ value: "" })}
                >
                  Add URI
                </Button>
              </Box>

              {fields.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No redirect URIs added yet.
                </Typography>
              )}

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {fields.map((field, index) => (
                  <Grid item xs={12} key={field.id}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Controller
                        name={`allowedRedirectUris.${index}.value`}
                        control={control}
                        render={({ field: inputField }) => (
                          <TextField
                            {...inputField}
                            fullWidth
                            size="small"
                            placeholder="https://example.com/auth/callback"
                            error={!!errors.allowedRedirectUris?.[index]?.value}
                            helperText={
                              errors.allowedRedirectUris?.[index]?.value?.message
                            }
                          />
                        )}
                      />
                      <IconButton
                        aria-label="remove redirect uri"
                        onClick={() => remove(index)}
                        sx={{ color: "error.main", mt: 0.25 }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating}
                sx={{
                  backgroundColor: "#000",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#333" },
                  "&:disabled": { backgroundColor: "#000", color: "#fff" },
                }}
              >
                {isUpdating ? "Saving..." : "Save Redirect URIs"}
              </Button>
            </form>
          </Grid>
        </Grid>
      )}

      <AlertMsg msgAlert={msgAlert} onCloseAlertMsg={handleCloseAlert} />
    </Container>
  );
};

export default RedirectUriSettings;
